/* Borderline winter 26/27 · the lookbook (P2: sections/bl-lookbook.liquid, snippets/bl-look-card / bl-look-piece /
   bl-look-drawer, assets/bl-lookbook.css). Prototype v83 winter-proto/assets/js/app.js (lookCardHTML, the look drawer).
   - a colour thumbnail turns its piece row to that colour: picture, links, name, "colour · price", the + and what it
     adds; the card's "Choose sizes · $total" follows;
   - + adds a one-size piece at once; a piece with sizes opens the drawer on that piece's size select;
   - "Choose sizes" (or a click on the photo) opens the drawer, a modal <dialog> from the right: the frames, one row
     per piece (size select with sold-out sizes disabled, its own Add, a stock line), the total and "Add the look";
     a missing size is flagged on its row and gets the focus;
   - adds go through POST /cart/add.js (items[]) and the theme's CartLinesUpdateEvent, as P0's card does, so the cart
     bubble and the cart drawer's lines update; then Horizon's cart drawer opens (a toast with a cart link where the
     theme has none). Esc and the backdrop close the drawer; focus goes back to the button that opened it. */
import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { getScrollTop, scrollTo as scrollPageTo } from '@theme/scroll-container';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const DONE_MS = 1400;
const TOAST_MS = 5000;

/**
 * @typedef {{ id: number, s: string, a: boolean, p: number, q: number }} Variant - id, size label, available, price (cents), stock (-1 untracked)
 * @typedef {{ t: string, u: string, k: string, v: string, c: string, o: string, img: string, set: string, a: boolean, p: number, pre?: boolean, ship?: string, vs: Variant[] }} Choice - one colour (or part) of a piece; pre: on pre-order, ship: "Nov 1"
 * @typedef {{ cur: number, note: string | null, ch: Choice[] }} Piece
 */

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} [dialog] - The drawer.
 * @property {HTMLElement} [drEyebrow] - "Look 01 · Snow Jacket + Snow Pants".
 * @property {HTMLElement} [drTitle] - The look's name in the drawer.
 * @property {HTMLElement} [drPhotos] - The frames.
 * @property {HTMLElement} [drList] - The piece rows.
 * @property {HTMLElement} [drError] - The add's error line (role=alert).
 * @property {HTMLElement} [drTotal] - The total.
 * @property {HTMLElement} [drCount] - "2 pieces".
 * @property {HTMLButtonElement} [drAll] - "Add the look".
 * @property {HTMLTemplateElement} [drRow] - The drawer row skeleton.
 * @property {HTMLScriptElement} [i18n] - Interface words, money format and currency.
 * @property {HTMLElement} [toast] - The toast and live region.
 * @property {HTMLElement} [toastMsg] - Its message.
 * @property {HTMLAnchorElement} [toastLink] - Its cart link.
 *
 * @extends Component<Refs>
 */
class BlLookbookComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** Parsed piece data per card row. @type {WeakMap<Element, Piece>} */
  #pieces = new WeakMap();

  /** The chosen size per look · piece · colour. @type {Map<string, number>} */
  #sizes = new Map();

  /** The look in the drawer and the button to give the focus back to. @type {{ look: HTMLElement, opener: HTMLElement | null } | null} */
  #current = null;

  /** @type {Record<string, string>} */
  #t = {};

  #busy = false;

  /** The page's scroll position when the drawer opened (restored after the lock, as Horizon's dialogs do). */
  #scrollY = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #toastTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#t = this.#json(this.refs.i18n) || {};
    const dialog = this.refs.dialog;
    if (dialog) {
      dialog.addEventListener('cancel', this.#onCancel, { signal });
      dialog.addEventListener('click', this.#onDialogClick, { signal });
      dialog.addEventListener('close', this.#onClose, { signal });
    }
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#t = this.#json(this.refs.i18n) || this.#t;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#toastTimer);
    const dialog = this.refs.dialog;
    if (dialog) {
      unlockScroll(dialog);
      if (dialog.open) dialog.close();
      dialog.classList.remove('is-in');
    }
    this.#current = null;
  }

  /* ---------- the card ---------- */

  /**
   * A colour thumbnail on a piece row: the row turns to that colour.
   * @param {MouseEvent} event
   */
  pickColour(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const row = button.closest('.bll-pc');
    const piece = this.#piece(row);
    const index = Number(button.dataset.choice);
    if (!(row instanceof HTMLElement) || !piece || !piece.ch[index] || piece.cur === index) return;

    piece.cur = index;
    row.dataset.cur = String(index);
    for (const swatch of row.querySelectorAll('.bll-pc__swb')) {
      const on = swatch === button;
      swatch.classList.toggle('is-on', on);
      swatch.setAttribute('aria-pressed', String(on));
    }
    this.#paintRow(row, piece);
    const look = row.closest('.bll-look');
    if (look instanceof HTMLElement) this.#paintTotal(look);
  }

  /**
   * The "+" on a piece: one size goes straight to the cart, sizes open the drawer on that piece.
   * @param {MouseEvent} event
   */
  addPiece(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const row = button.closest('.bll-pc');
    const look = row?.closest('.bll-look');
    const piece = this.#piece(row);
    const choice = piece?.ch[piece.cur];
    if (!(look instanceof HTMLElement) || !(row instanceof HTMLElement) || !choice?.a) return;

    if (choice.vs.length === 1) {
      this.#add([choice.vs[0].id], { source: button, flash: button });
      return;
    }
    this.#open(look, button, row.dataset.piece ?? null);
  }

  /**
   * "Choose sizes" or the photo: the drawer for this look.
   * @param {MouseEvent} event
   */
  openLook(event) {
    const look = /** @type {HTMLElement} */ (event.target).closest('.bll-look');
    if (!(look instanceof HTMLElement)) return;
    const opener = look.querySelector('.bll-look__cta');
    this.#open(look, opener instanceof HTMLElement ? opener : null, null);
  }

  /* ---------- the drawer ---------- */

  closeLook() {
    this.#close();
  }

  /**
   * A size was chosen in the drawer.
   * @param {Event} event
   */
  chooseSize(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const item = select.closest('.bll-dr__row');
    const look = this.#current?.look;
    if (!(item instanceof HTMLElement) || !look) return;
    const row = this.#rowOf(look, item);
    const piece = this.#piece(row);
    if (!(row instanceof HTMLElement) || !piece) return;

    const key = this.#key(look, row, piece);
    if (select.value) this.#sizes.set(key, Number(select.value));
    else this.#sizes.delete(key);
    this.#flag(item, false);
    this.#paintDrawerRow(item, look, row, piece);
    this.#paintDrawerTotal();
    this.#paintTotal(look);
  }

  /**
   * One piece's Add in the drawer.
   * @param {MouseEvent} event
   */
  addOne(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const item = button.closest('.bll-dr__row');
    const look = this.#current?.look;
    if (!(item instanceof HTMLElement) || !look) return;
    const row = this.#rowOf(look, item);
    const piece = this.#piece(row);
    if (!(row instanceof HTMLElement) || !piece || !piece.ch[piece.cur]?.a) return;

    const variant = this.#chosen(look, row, piece);
    if (!variant) {
      this.#flag(item, true);
      item.querySelector('select')?.focus();
      return;
    }
    this.#add([variant.id], { source: button, inDrawer: true });
  }

  /**
   * "Add the look": every piece in stock at its chosen size, in one request.
   * @param {MouseEvent} event
   */
  addLook(event) {
    const look = this.#current?.look;
    const list = this.refs.drList;
    if (!look || !list) return;

    /** @type {number[]} */
    const ids = [];
    /** @type {HTMLElement | null} */
    let missing = null;
    for (const item of /** @type {HTMLElement[]} */ ([...list.querySelectorAll('.bll-dr__row')])) {
      const row = this.#rowOf(look, item);
      const piece = this.#piece(row);
      if (!(row instanceof HTMLElement) || !piece || !piece.ch[piece.cur]?.a) continue;
      const variant = this.#chosen(look, row, piece);
      this.#flag(item, !variant);
      if (variant) ids.push(variant.id);
      else missing ??= item;
    }
    if (missing) {
      missing.querySelector('select')?.focus();
      return;
    }
    if (ids.length) this.#add(ids, { source: /** @type {HTMLElement} */ (event.target), inDrawer: true });
  }

  /**
   * @param {HTMLElement} look
   * @param {HTMLElement | null} opener - Gets the focus back on close.
   * @param {string | null} pieceIndex - Focus this piece's size select.
   */
  #open(look, opener, pieceIndex) {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#current = { look, opener };
    this.#render(look);
    if (!dialog.open) {
      this.#scrollY = getScrollTop();
      lockScroll(dialog);
      dialog.showModal();
      // two frames: the closed position is painted first, so the panel slides in
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-in')));
    }

    const select = pieceIndex == null ? null : dialog.querySelector(`.bll-dr__row[data-piece="${pieceIndex}"] select`);
    const focus = select || dialog.querySelector('.bll-dr__x');
    if (focus instanceof HTMLElement) focus.focus({ preventScroll: true });
  }

  /**
   * Slide out, close, give the focus back.
   * @param {{ restore?: boolean }} [options]
   */
  async #close({ restore = true } = {}) {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    const current = this.#current;
    this.#current = null;

    dialog.classList.remove('is-in');
    if (!reducedMotion.matches) await this.#settle(dialog);
    dialog.close();
    unlockScroll(dialog);
    scrollPageTo({ top: this.#scrollY, behavior: 'instant' });
    if (restore && current?.opener?.isConnected) current.opener.focus({ preventScroll: true });
  }

  /** Esc: the same slide out. @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  /** A press on the backdrop (outside the panel) closes the drawer. @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const dialog = this.refs.dialog;
    if (!dialog || event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) this.#close();
  };

  /** The browser closed the dialog itself (a repeated Esc): tidy up the same way. */
  #onClose = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    dialog.classList.remove('is-in');
    unlockScroll(dialog);
    const current = this.#current;
    this.#current = null;
    if (!current) return;
    scrollPageTo({ top: this.#scrollY, behavior: 'instant' });
    if (current.opener?.isConnected) current.opener.focus({ preventScroll: true });
  };

  /**
   * Fill the drawer for a look.
   * @param {HTMLElement} look
   */
  #render(look) {
    const { drEyebrow, drTitle, drPhotos, drList, drError } = this.refs;
    const rows = /** @type {HTMLElement[]} */ ([...look.querySelectorAll('.bll-pc')]);
    const kinds = [
      ...new Set(
        rows
          .map((row) => {
            const piece = this.#piece(row);
            return piece?.ch[piece.cur]?.k || '';
          })
          .filter(Boolean)
      ),
    ];
    const lead = [this.#t.look, look.dataset.n].filter(Boolean).join(' ');
    // a one-piece look (a set sold whole) names no kind: its product type would read as one garment
    const kind = rows.length > 1 ? kinds.join(' + ') : '';
    if (drEyebrow) drEyebrow.textContent = [lead, kind].filter(Boolean).join(' · ');
    if (drTitle) drTitle.textContent = look.querySelector('.bll-look__name')?.textContent?.trim() || '';

    if (drPhotos) {
      const frames = /** @type {HTMLImageElement[]} */ ([...look.querySelectorAll('.bll-look__pic')]);
      drPhotos.replaceChildren(
        ...frames.map((frame, index) => {
          const copy = /** @type {HTMLImageElement} */ (frame.cloneNode());
          copy.className = 'bll-dr__photo';
          copy.loading = 'eager';
          if (copy.srcset) copy.sizes = '(max-width: 520px) 50vw, 236px';
          if (index > 0) copy.alt = '';
          return copy;
        })
      );
    }
    if (drList) {
      drList.replaceChildren(
        ...rows.map((row) => this.#drawerRow(look, row)).filter((item) => item instanceof HTMLElement)
      );
    }
    if (drError) {
      drError.hidden = true;
      drError.textContent = '';
    }
    this.#paintDrawerTotal();
  }

  /**
   * One drawer row from a card row.
   * @param {HTMLElement} look
   * @param {HTMLElement} row
   * @returns {HTMLElement | null}
   */
  #drawerRow(look, row) {
    const piece = this.#piece(row);
    const template = this.refs.drRow;
    if (!piece || !(template instanceof HTMLTemplateElement)) return null;
    const choice = piece.ch[piece.cur];
    const item = template.content.firstElementChild?.cloneNode(true);
    if (!(item instanceof HTMLElement) || !choice) return null;

    const index = row.dataset.piece || '0';
    const id = `${look.id}-dr-${index}`;
    item.dataset.piece = index;

    const pic = item.querySelector('.bll-dr__pic');
    if (pic instanceof HTMLImageElement) {
      if (choice.img) {
        if (choice.set) {
          pic.srcset = choice.set;
          pic.sizes = '72px';
        }
        pic.src = choice.img;
      } else {
        pic.remove();
      }
    }
    for (const link of item.querySelectorAll('a')) link.setAttribute('href', choice.u);
    this.#text(item, '.bll-dr__brand', [choice.k, choice.v].filter(Boolean).join(' · '));
    this.#text(item, '.bll-dr__name', choice.t);
    // a part's value is the row's kind already ("Jacket · Yeyepop"): not again before the size
    this.#text(item, '.bll-dr__colour', choice.c && choice.c !== choice.k ? choice.c : '');
    this.#text(item, '.bll-dr__pnote', piece.note || '');

    const size = item.querySelector('.bll-dr__size');
    const select = item.querySelector('select');
    if (choice.vs.length > 1 && size && select instanceof HTMLSelectElement) {
      select.id = `${id}-size`;
      this.#text(item, '.bll-dr__size-l', choice.o || '');
      const options = choice.vs.map((variant) => {
        const option = new Option(variant.a ? variant.s : `${variant.s} · ${this.#t.soldOut || ''}`, String(variant.id));
        option.disabled = !variant.a;
        return option;
      });
      select.replaceChildren(new Option(this.#t.pick || '—', ''), ...options);
      const chosen = this.#chosen(look, row, piece);
      select.value = chosen ? String(chosen.id) : '';
      select.disabled = !choice.a;
      const miss = item.querySelector('.bll-dr__miss');
      if (miss) {
        miss.id = `${id}-miss`;
        select.setAttribute('aria-describedby', miss.id);
      }
    } else {
      size?.remove();
    }

    const add = item.querySelector('.bll-dr__add');
    if (add instanceof HTMLButtonElement) {
      add.disabled = !choice.a;
      add.setAttribute('aria-label', this.#say('add', choice.t));
    }
    this.#paintDrawerRow(item, look, row, piece);
    return item;
  }

  /**
   * A drawer row's price and stock line, after a size change.
   * @param {HTMLElement} item
   * @param {HTMLElement} look
   * @param {HTMLElement} row
   * @param {Piece} piece
   */
  #paintDrawerRow(item, look, row, piece) {
    const choice = piece.ch[piece.cur];
    const variant = this.#chosen(look, row, piece);
    this.#text(item, '.bll-dr__price', choice.a ? this.#money(variant ? variant.p : choice.p) : '');

    const status = item.querySelector('.bll-dr__status');
    if (!status) return;
    let state = '';
    let words = '';
    if (!choice.a) {
      state = 'out';
      words = this.#t.soldOut || '';
    } else if (variant && choice.pre) {
      state = 'pre';
      words = choice.ship
        ? (this.#t.preorderShips || '').replace('%date%', choice.ship)
        : this.#t.preorder || '';
    } else if (variant) {
      state = variant.q === 1 ? 'low' : 'ok';
      words = (variant.q === 1 ? this.#t.left : this.#t.inStock) || '';
    }
    status.replaceChildren();
    if (!words) return;
    const dot = document.createElement('i');
    dot.className = `bll-dr__dot bll-dr__dot--${state}`;
    dot.setAttribute('aria-hidden', 'true');
    status.append(dot, words);
  }

  #paintDrawerTotal() {
    const look = this.#current?.look;
    const { drTotal, drCount, drAll } = this.refs;
    if (!look) return;
    const { total, count } = this.#total(look);
    if (drTotal) drTotal.textContent = this.#money(total);
    if (drCount) drCount.textContent = `${count} ${(count === 1 ? this.#t.piecesOne : this.#t.piecesOther) || ''}`.trim();
    if (drAll instanceof HTMLButtonElement) drAll.disabled = count === 0 || this.#busy;
  }

  /**
   * Mark a drawer row whose size is missing (or clear it).
   * @param {HTMLElement} item
   * @param {boolean} on
   */
  #flag(item, on) {
    const select = item.querySelector('select');
    const miss = item.querySelector('.bll-dr__miss');
    item.classList.toggle('is-missing', on);
    if (select) {
      if (on) select.setAttribute('aria-invalid', 'true');
      else select.removeAttribute('aria-invalid');
    }
    if (miss instanceof HTMLElement) {
      miss.hidden = !on;
      miss.textContent = on ? this.#say('choose', item.querySelector('.bll-dr__name')?.textContent || '') : '';
    }
  }

  /* ---------- cart ---------- */

  /**
   * Add variants (one of each) through the theme's cart event contract.
   * @param {number[]} ids
   * @param {{ source?: HTMLElement | null, flash?: HTMLElement | null, inDrawer?: boolean }} options
   */
  async #add(ids, { source = null, flash = null, inDrawer = false }) {
    if (this.#busy || !ids.length) return;
    this.#busy = true;
    source?.setAttribute('aria-busy', 'true');
    const { drError, drAll } = this.refs;
    if (inDrawer && drError) {
      drError.hidden = true;
      drError.textContent = '';
    }
    if (drAll instanceof HTMLButtonElement && inDrawer) drAll.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const origin = source || this;
    const deferred = CartLinesUpdateEvent.createPromise();
    origin.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    let ok = false;
    let message = '';
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })), sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((cartResponse) => cartResponse.json());
      const detail = {
        items: cart.items,
        source: 'bl-lookbook',
        sourceId: this.dataset.sectionId,
        itemCount: ids.length,
      };

      if (result.status) {
        message = result.description || result.message || this.#t.error || '';
        origin.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, didError: true } });
      } else {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, sections: result.sections, didError: false },
        });
        ok = true;
      }
    } catch (error) {
      deferred.reject(error);
      message = this.#t.error || '';
      origin.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#busy = false;
      source?.removeAttribute('aria-busy');
      this.#paintDrawerTotal();
    }

    if (flash) this.#flash(flash, ok);
    if (!ok) {
      if (inDrawer && drError) {
        drError.textContent = message;
        drError.hidden = false;
      } else {
        this.#toast(message, false);
      }
      return;
    }

    const said = ids.length === 1 ? this.#t.addedOne : (this.#t.addedOther || '').replace('7777', String(ids.length));
    if (inDrawer) await this.#close({ restore: true });
    this.#showCart(said || '');
  }

  /**
   * Open Horizon's cart drawer; without one, the toast with a cart link.
   * @param {string} message
   */
  #showCart(message) {
    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.open === 'function') {
      if (!drawer.hasAttribute('open')) drawer.open();
      this.#announce(message);
      return;
    }
    this.#toast(message, true);
  }

  /**
   * The + answers for 1.4 s: ✓ when the line went in, ! when it did not (P0's card does the same).
   * @param {HTMLElement} button
   * @param {boolean} ok
   */
  #flash(button, ok) {
    const glyph = button.querySelector('span');
    button.classList.add('is-done');
    if (glyph) glyph.textContent = ok ? '✓' : '!';
    setTimeout(() => {
      button.classList.remove('is-done');
      if (glyph) glyph.textContent = '+';
    }, DONE_MS);
  }

  /**
   * @param {string} message
   * @param {boolean} withLink
   */
  #toast(message, withLink) {
    const { toast, toastMsg, toastLink } = this.refs;
    if (!toast || !toastMsg) return;
    toastMsg.textContent = message;
    if (toastLink) toastLink.hidden = !withLink;
    toast.classList.add('is-on');
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => {
      toast.classList.remove('is-on');
      if (toastLink) toastLink.hidden = true;
    }, TOAST_MS);
  }

  /** Say it in the live region without showing the toast. @param {string} message */
  #announce(message) {
    const { toastMsg } = this.refs;
    if (!toastMsg || !message) return;
    toastMsg.textContent = '';
    requestAnimationFrame(() => {
      toastMsg.textContent = message;
    });
  }

  /* ---------- data ---------- */

  /**
   * The piece data of a card row (parsed once).
   * @param {Element | null | undefined} row
   * @returns {Piece | null}
   */
  #piece(row) {
    if (!(row instanceof HTMLElement)) return null;
    let piece = this.#pieces.get(row);
    if (!piece) {
      const data = this.#json(row.querySelector('script[data-bll-piece]'));
      if (!data || !Array.isArray(data.ch) || !data.ch.length) return null;
      piece = /** @type {Piece} */ (data);
      piece.cur = Math.min(Math.max(0, Number(row.dataset.cur) || 0), piece.ch.length - 1);
      this.#pieces.set(row, piece);
    }
    return piece;
  }

  /**
   * The card row behind a drawer row.
   * @param {HTMLElement} look
   * @param {HTMLElement} item
   */
  #rowOf(look, item) {
    return look.querySelector(`.bll-pc[data-piece="${item.dataset.piece}"]`);
  }

  /**
   * @param {HTMLElement} look
   * @param {HTMLElement} row
   * @param {Piece} piece
   */
  #key(look, row, piece) {
    return `${look.id}|${row.dataset.piece}|${piece.cur}`;
  }

  /**
   * The variant a piece would add: its only one, or the size chosen in the drawer.
   * @param {HTMLElement} look
   * @param {HTMLElement} row
   * @param {Piece} piece
   * @returns {Variant | null}
   */
  #chosen(look, row, piece) {
    const choice = piece.ch[piece.cur];
    if (!choice) return null;
    if (choice.vs.length === 1) return choice.vs[0].a ? choice.vs[0] : null;
    const id = this.#sizes.get(this.#key(look, row, piece));
    return choice.vs.find((variant) => variant.id === id && variant.a) || null;
  }

  /**
   * The look's price: each piece in stock at its chosen size, else at its colour's price.
   * @param {HTMLElement} look
   */
  #total(look) {
    let total = 0;
    let count = 0;
    for (const row of /** @type {HTMLElement[]} */ ([...look.querySelectorAll('.bll-pc')])) {
      const piece = this.#piece(row);
      const choice = piece?.ch[piece.cur];
      if (!piece || !choice?.a) continue;
      const variant = this.#chosen(look, row, piece);
      total += variant ? variant.p : choice.p;
      count += 1;
    }
    return { total, count };
  }

  /** @param {HTMLElement} look */
  #paintTotal(look) {
    const { total } = this.#total(look);
    const out = look.querySelector('[data-bll-total]');
    const sum = look.querySelector('.bll-look__sum');
    if (out) out.textContent = this.#money(total);
    if (sum instanceof HTMLElement) sum.hidden = total <= 0;
  }

  /**
   * A card row turned to its current colour.
   * @param {HTMLElement} row
   * @param {Piece} piece
   */
  #paintRow(row, piece) {
    const choice = piece.ch[piece.cur];
    const pic = row.querySelector('.bll-pc__pic');
    if (pic instanceof HTMLImageElement && choice.img) {
      pic.srcset = choice.set || '';
      pic.src = choice.img;
    }
    for (const link of row.querySelectorAll('a[href]')) link.setAttribute('href', choice.u);
    this.#text(row, '.bll-pc__name', choice.t);
    this.#text(
      row,
      '.bll-pc__meta',
      [choice.c, choice.a ? this.#money(choice.p) : this.#t.soldOut].filter(Boolean).join(' · ')
    );
    const add = row.querySelector('.bll-pc__add');
    if (add instanceof HTMLElement) {
      add.hidden = !choice.a;
      add.setAttribute('aria-label', this.#say(choice.vs.length > 1 ? 'choose' : 'add', choice.t));
    }
  }

  /* ---------- helpers ---------- */

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#t.moneyFormat || '${{amount}}', this.#t.currency || 'CAD');
  }

  /**
   * @param {string} key
   * @param {string} title
   */
  #say(key, title) {
    return (this.#t[key] || '').replace('%title%', title);
  }

  /**
   * @param {ParentNode} root
   * @param {string} selector
   * @param {string} text
   */
  #text(root, selector, text) {
    const element = root.querySelector(selector);
    if (element) element.textContent = text;
  }

  /** @param {Element | null | undefined} script */
  #json(script) {
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  /** @param {Element} element */
  #settle(element) {
    return new Promise((resolve) => {
      /** @param {TransitionEvent} event */
      const onEnd = (event) => {
        if (event.target === element && event.propertyName === 'transform') done();
      };
      const done = () => {
        clearTimeout(timer);
        element.removeEventListener('transitionend', onEnd);
        resolve(undefined);
      };
      const timer = setTimeout(done, 420);
      element.addEventListener('transitionend', onEnd);
    });
  }

  /** The theme editor selected a look block: bring its card into the rail's view. @param {Event} event */
  #onBlockSelect = (event) => {
    const item = event.target instanceof Element ? event.target.closest('.bll__item') : null;
    if (item instanceof HTMLElement) {
      item.scrollIntoView({ block: 'nearest', inline: 'start', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  };
}

if (!customElements.get('bl-lookbook-component')) {
  customElements.define('bl-lookbook-component', BlLookbookComponent);
}
